/**
 * EquiposCatalogApiClient -- Catalogo de equipos a 2 niveles (modelos + unidades)
 * y historial de asignaciones negocio ↔ unidad.
 *
 * ESTADO: MOCK en memoria. La API de respaldo todavia no existe.
 * Todos los metodos que van a pegar contra un endpoint real estan marcados con
 * el prefijo "NEEDS-API:" para que Ares / ARGYS-API los implementen en NexoAPI.
 *
 * Modelo de datos (propuesto):
 *   equipment_models       (catalogo compartido)
 *     id, name, brand, type, description, image_url?, created_at, updated_at
 *   equipment_units        (instancias con serial)
 *     id, model_id, serial (UNIQUE), status, notes, created_at, updated_at
 *   equipment_assignments  (historial negocio ↔ unidad)
 *     id, unit_id, business_id, assigned_at, returned_at?, assigned_by, notes?
 *
 * Convenciones de respuesta (compatibles con CrudResult / ApiBase):
 *   { ok: true,  data: ... }      → exito
 *   { ok: false, error: '...' }   → error controlado
 *
 * @module infrastructure/api/EquiposCatalogApiClient
 */

// ---------------------------------------------------------------------------
// Mock store (in-memory). Se reinicia al recargar la pagina.
// ---------------------------------------------------------------------------
const EQUIPMENT_TYPES = ['impresora', 'computador', 'tablet', 'caja_registradora', 'terminal_pos', 'celular', 'router', 'tv_monitor', 'otro'];
const UNIT_STATUSES   = ['disponible', 'asignado', 'en_mantenimiento', 'dado_de_baja'];

let _nextModelSeq = 4;
let _nextUnitSeq  = 6;
let _nextAssignSeq = 3;

const _now = () => new Date().toISOString();

/** @type {Array<{id,string, name:string, brand:string, type:string, description:string, image_url:?string, created_at:string, updated_at:string}>} */
const _models = [
  { id: 'mdl-001', name: 'Impresora Termica SAT15TUS', brand: 'SAT',   type: 'impresora',  description: 'Impresora termica 80mm, USB + Ethernet', image_url: null, created_at: _now(), updated_at: _now() },
  { id: 'mdl-002', name: 'Dell Inspiron 15 3520',      brand: 'Dell',  type: 'computador', description: 'Notebook i5 11va gen, 8GB RAM, 256GB SSD',  image_url: null, created_at: _now(), updated_at: _now() },
  { id: 'mdl-003', name: 'Router MikroTik hAP ac2',    brand: 'MikroTik', type: 'router', description: 'Router WiFi dual-band con RouterOS',       image_url: null, created_at: _now(), updated_at: _now() },
];

/** @type {Array<{id:string, model_id:string, serial:string, status:string, notes:?string, created_at:string, updated_at:string}>} */
const _units = [
  { id: 'unt-001', model_id: 'mdl-001', serial: 'SAT-0001', status: 'asignado',    notes: null, created_at: _now(), updated_at: _now() },
  { id: 'unt-002', model_id: 'mdl-001', serial: 'SAT-0002', status: 'disponible',  notes: null, created_at: _now(), updated_at: _now() },
  { id: 'unt-003', model_id: 'mdl-002', serial: 'DLL-0001', status: 'asignado',    notes: 'Equipo de caja',          created_at: _now(), updated_at: _now() },
  { id: 'unt-004', model_id: 'mdl-002', serial: 'DLL-0002', status: 'en_mantenimiento', notes: 'Cambio de bateria',  created_at: _now(), updated_at: _now() },
  { id: 'unt-005', model_id: 'mdl-003', serial: 'MKT-0001', status: 'disponible',  notes: null, created_at: _now(), updated_at: _now() },
];

/** @type {Array<{id:string, unit_id:string, business_id:string, assigned_at:string, returned_at:?string, assigned_by:string, notes:?string}>} */
const _assignments = [
  { id: 'asg-001', unit_id: 'unt-001', business_id: 'demo-biz-1', assigned_at: _now(), returned_at: null, assigned_by: 'ARGYS-PANEL-001', notes: null },
  { id: 'asg-002', unit_id: 'unt-003', business_id: 'demo-biz-1', assigned_at: _now(), returned_at: null, assigned_by: 'ARGYS-PANEL-001', notes: null },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const ok = (data)   => ({ ok: true, data });
const fail = (error) => ({ ok: false, error });
const clone = (v) => (v == null ? v : JSON.parse(JSON.stringify(v)));
const delay = (ms = 60) => new Promise(r => setTimeout(r, ms));

function _computeModelCounts(modelId) {
  const units = _units.filter(u => u.model_id === modelId);
  const assignedIds = new Set(
    _assignments.filter(a => a.returned_at == null).map(a => a.unit_id)
  );
  return {
    total:            units.length,
    disponibles:      units.filter(u => u.status === 'disponible' && !assignedIds.has(u.id)).length,
    asignadas:        units.filter(u => assignedIds.has(u.id)).length,
    en_mantenimiento: units.filter(u => u.status === 'en_mantenimiento').length,
    dadas_de_baja:    units.filter(u => u.status === 'dado_de_baja').length,
  };
}

// ---------------------------------------------------------------------------
// API PUBLICA
// ---------------------------------------------------------------------------
export const EquiposCatalogApiClient = {

  // PANEL-EQUIPOS-AVISO-T1: este cliente es un MOCK en memoria. Mientras sea true, las vistas lo
  // dicen y no dejan registrar (application/equipos/maqueta.js). Pasa a false con EQUIPOS-API-T1.
  ES_MAQUETA: true,

  // Catalogo del cliente (constantes de UI)
  TYPES:          EQUIPMENT_TYPES,
  UNIT_STATUSES:  UNIT_STATUSES,

  // ---------------- Modelos ----------------

  /**
   * NEEDS-API: GET /api/v1/equipment-models
   * Response: { success: true, data: Array<EquipmentModel & { counts: {total, disponibles, asignadas, ...} }> }
   * Notas para backend: JOIN con equipment_units + equipment_assignments para calcular counts,
   * o endpoint auxiliar GET /api/v1/equipment-models/:id/counts.
   */
  async listModels() {
    await delay();
    const data = _models.map(m => ({ ...clone(m), counts: _computeModelCounts(m.id) }));
    return ok(data);
  },

  /**
   * NEEDS-API: GET /api/v1/equipment-models/:id
   * Response: { success: true, data: EquipmentModel }
   */
  async getModel(id) {
    await delay();
    const m = _models.find(x => x.id === id);
    return m ? ok({ ...clone(m), counts: _computeModelCounts(m.id) }) : fail('Modelo no encontrado');
  },

  /**
   * NEEDS-API: POST /api/v1/equipment-models
   * Body: { name, brand, type, description?, image_url? }
   * Response: { success: true, data: EquipmentModel }
   * Validaciones backend: name y type requeridos, type ∈ EQUIPMENT_TYPES.
   */
  async createModel(data) {
    await delay();
    if (!data?.name || !data?.type) return fail('name y type son obligatorios');
    const m = {
      id: `mdl-${String(_nextModelSeq++).padStart(3, '0')}`,
      name: String(data.name),
      brand: String(data.brand || ''),
      type: String(data.type),
      description: String(data.description || ''),
      image_url: data.image_url || null,
      created_at: _now(),
      updated_at: _now(),
    };
    _models.push(m);
    return ok({ ...clone(m), counts: _computeModelCounts(m.id) });
  },

  /**
   * NEEDS-API: PUT /api/v1/equipment-models/:id
   * Body: { name?, brand?, type?, description?, image_url? }
   * Response: { success: true, data: EquipmentModel }
   */
  async updateModel(id, data) {
    await delay();
    const idx = _models.findIndex(m => m.id === id);
    if (idx === -1) return fail('Modelo no encontrado');
    _models[idx] = { ..._models[idx], ...data, id, updated_at: _now() };
    return ok({ ...clone(_models[idx]), counts: _computeModelCounts(id) });
  },

  /**
   * NEEDS-API: DELETE /api/v1/equipment-models/:id
   * Response: { success: true }
   * Notas: la API debe rechazar (409) si existen unidades asociadas.
   */
  async deleteModel(id) {
    await delay();
    if (_units.some(u => u.model_id === id)) return fail('No se puede eliminar: el modelo tiene unidades asociadas');
    const idx = _models.findIndex(m => m.id === id);
    if (idx === -1) return fail('Modelo no encontrado');
    _models.splice(idx, 1);
    return ok(true);
  },

  // ---------------- Unidades ----------------

  /**
   * NEEDS-API: GET /api/v1/equipment-models/:modelId/units
   *   (o bien: GET /api/v1/equipment-units?modelId=...)
   * Response: { success: true, data: Array<EquipmentUnit & {
   *   currentAssignment: { id, business_id, business_name, assigned_at } | null
   * }> }
   */
  async listUnits(modelId) {
    await delay();
    const units = _units.filter(u => u.model_id === modelId);
    const data = units.map(u => {
      const current = _assignments.find(a => a.unit_id === u.id && a.returned_at == null);
      return {
        ...clone(u),
        currentAssignment: current
          ? { id: current.id, business_id: current.business_id, business_name: null, assigned_at: current.assigned_at }
          : null,
      };
    });
    return ok(data);
  },

  /**
   * NEEDS-API: GET /api/v1/equipment-units/:id
   * Response: { success: true, data: EquipmentUnit }
   */
  async getUnit(id) {
    await delay();
    const u = _units.find(x => x.id === id);
    return u ? ok(clone(u)) : fail('Unidad no encontrada');
  },

  /**
   * NEEDS-API: POST /api/v1/equipment-models/:modelId/units
   * Body: { serial, status?, notes? }
   * Response: { success: true, data: EquipmentUnit }
   * Validaciones backend: serial UNIQUE global, status ∈ UNIT_STATUSES.
   */
  async createUnit(modelId, data) {
    await delay();
    if (!_models.find(m => m.id === modelId)) return fail('Modelo no encontrado');
    if (!data?.serial) return fail('serial es obligatorio');
    if (_units.some(u => u.serial === data.serial)) return fail('El serial ya existe');
    const u = {
      id: `unt-${String(_nextUnitSeq++).padStart(3, '0')}`,
      model_id: modelId,
      serial: String(data.serial),
      status: UNIT_STATUSES.includes(data.status) ? data.status : 'disponible',
      notes: data.notes || null,
      created_at: _now(),
      updated_at: _now(),
    };
    _units.push(u);
    return ok(clone(u));
  },

  /**
   * NEEDS-API: PUT /api/v1/equipment-units/:id
   * Body: { serial?, status?, notes? }
   * Response: { success: true, data: EquipmentUnit }
   */
  async updateUnit(unitId, data) {
    await delay();
    const idx = _units.findIndex(u => u.id === unitId);
    if (idx === -1) return fail('Unidad no encontrada');
    if (data?.serial && data.serial !== _units[idx].serial
      && _units.some(u => u.serial === data.serial)) {
      return fail('El serial ya existe');
    }
    _units[idx] = { ..._units[idx], ...data, id: unitId, updated_at: _now() };
    return ok(clone(_units[idx]));
  },

  /**
   * NEEDS-API: DELETE /api/v1/equipment-units/:id
   * Response: { success: true }
   * Notas: la API deberia rechazar si la unidad tiene una asignacion activa.
   */
  async deleteUnit(unitId) {
    await delay();
    if (_assignments.some(a => a.unit_id === unitId && a.returned_at == null)) {
      return fail('No se puede eliminar: la unidad tiene una asignacion activa');
    }
    const idx = _units.findIndex(u => u.id === unitId);
    if (idx === -1) return fail('Unidad no encontrada');
    _units.splice(idx, 1);
    return ok(true);
  },

  // ---------------- Asignaciones ----------------

  /**
   * NEEDS-API: POST /api/v1/equipment-assignments
   * Body: { unit_id, business_id, assigned_by?, notes? }
   * Response: { success: true, data: EquipmentAssignment }
   * Side-effects: marcar equipment_units.status = 'asignado' si estaba disponible.
   * Validaciones: unit_id sin asignacion activa (returned_at IS NULL).
   */
  async assignUnit(unitId, businessId, meta = {}) {
    await delay();
    const unit = _units.find(u => u.id === unitId);
    if (!unit) return fail('Unidad no encontrada');
    if (_assignments.some(a => a.unit_id === unitId && a.returned_at == null)) {
      return fail('La unidad ya esta asignada a un negocio');
    }
    const a = {
      id: `asg-${String(_nextAssignSeq++).padStart(3, '0')}`,
      unit_id: unitId,
      business_id: String(businessId),
      assigned_at: _now(),
      returned_at: null,
      assigned_by: meta.assigned_by || 'panel',
      notes: meta.notes || null,
    };
    _assignments.push(a);
    unit.status = 'asignado';
    unit.updated_at = _now();
    return ok(clone(a));
  },

  /**
   * NEEDS-API: PATCH /api/v1/equipment-assignments/:id/return
   *   Body: { notes? }
   *   Response: { success: true, data: EquipmentAssignment }
   * Side-effects: marcar equipment_units.status = 'disponible'.
   */
  async returnUnit(assignmentId, meta = {}) {
    await delay();
    const a = _assignments.find(x => x.id === assignmentId);
    if (!a) return fail('Asignacion no encontrada');
    if (a.returned_at) return fail('La asignacion ya fue devuelta');
    a.returned_at = _now();
    if (meta.notes) a.notes = a.notes ? `${a.notes} | ${meta.notes}` : meta.notes;
    const unit = _units.find(u => u.id === a.unit_id);
    if (unit) { unit.status = 'disponible'; unit.updated_at = _now(); }
    return ok(clone(a));
  },

  /**
   * NEEDS-API: GET /api/v1/businesses/:businessId/equipment
   *   Response: { success: true, data: Array<{
   *     assignment: EquipmentAssignment,
   *     unit: EquipmentUnit,
   *     model: EquipmentModel,
   *   }> }
   * Notas: devuelve SOLO las asignaciones activas (returned_at IS NULL).
   */
  async getBusinessEquipment(businessId) {
    await delay();
    const active = _assignments.filter(a => a.business_id === businessId && a.returned_at == null);
    const data = active.map(a => {
      const unit  = _units.find(u => u.id === a.unit_id);
      const model = unit ? _models.find(m => m.id === unit.model_id) : null;
      return { assignment: clone(a), unit: clone(unit), model: clone(model) };
    });
    return ok(data);
  },

  /**
   * NEEDS-API: GET /api/v1/businesses/:businessId/equipment/history
   *   Query opt: ?limit=20
   *   Response: { success: true, data: Array<{ assignment, unit, model }> }
   * Incluye asignaciones devueltas (returned_at NOT NULL) ordenadas DESC.
   */
  async getBusinessHistory(businessId, limit = 20) {
    await delay();
    const history = _assignments
      .filter(a => a.business_id === businessId && a.returned_at != null)
      .sort((x, y) => (y.returned_at || '').localeCompare(x.returned_at || ''))
      .slice(0, limit);
    const data = history.map(a => {
      const unit  = _units.find(u => u.id === a.unit_id);
      const model = unit ? _models.find(m => m.id === unit.model_id) : null;
      return { assignment: clone(a), unit: clone(unit), model: clone(model) };
    });
    return ok(data);
  },

  /**
   * NEEDS-API: GET /api/v1/equipment-units/:id/assignments
   *   Response: { success: true, data: Array<EquipmentAssignment> }
   */
  async getAssignmentHistory(unitId) {
    await delay();
    const data = _assignments
      .filter(a => a.unit_id === unitId)
      .sort((x, y) => (y.assigned_at || '').localeCompare(x.assigned_at || ''));
    return ok(clone(data));
  },

  /**
   * NEEDS-API: GET /api/v1/equipment-units?status=disponible
   *   Response: { success: true, data: Array<{ unit, model }> }
   * Usado por el flujo "Asignar equipo" en NegocioDetailView para elegir entre
   * unidades libres. Debe excluir unidades con asignacion activa.
   */
  async listAvailableUnits() {
    await delay();
    const assignedIds = new Set(
      _assignments.filter(a => a.returned_at == null).map(a => a.unit_id)
    );
    const data = _units
      .filter(u => !assignedIds.has(u.id) && u.status !== 'dado_de_baja' && u.status !== 'en_mantenimiento')
      .map(u => ({ unit: clone(u), model: clone(_models.find(m => m.id === u.model_id)) }));
    return ok(data);
  },
};

// ---------------------------------------------------------------------------
// Lista de endpoints NEEDS-API (resumen para el reporte a Ares):
//   GET    /api/v1/equipment-models
//   GET    /api/v1/equipment-models/:id
//   POST   /api/v1/equipment-models
//   PUT    /api/v1/equipment-models/:id
//   DELETE /api/v1/equipment-models/:id
//   GET    /api/v1/equipment-models/:modelId/units
//   GET    /api/v1/equipment-units/:id
//   POST   /api/v1/equipment-models/:modelId/units
//   PUT    /api/v1/equipment-units/:id
//   DELETE /api/v1/equipment-units/:id
//   GET    /api/v1/equipment-units?status=disponible
//   GET    /api/v1/equipment-units/:id/assignments
//   POST   /api/v1/equipment-assignments
//   PATCH  /api/v1/equipment-assignments/:id/return
//   GET    /api/v1/businesses/:businessId/equipment
//   GET    /api/v1/businesses/:businessId/equipment/history
// ---------------------------------------------------------------------------
